function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useRef, useEffect, CSSProperties, Ref, FC, useState } from 'react';
import cn from 'classnames';
import { Icon } from '../Icon';
import { useMaskedInput } from '../../hooks/useMaskedInput';
import { useForkRef } from '../../hooks/useForkRef';
require("./AmountCounter.css");
var styles = {
  "Root": "AmountCounter-module-Root_3gp",
  "BtnDown": "AmountCounter-module-BtnDown_3Fh",
  "BtnUp": "AmountCounter-module-BtnUp_KZQ",
  "Input": "AmountCounter-module-Input_2i6",
  "Icon": "AmountCounter-module-Icon_3IG",
  "neutralVariant": "AmountCounter-module-neutralVariant_10F",
  "accentVariant": "AmountCounter-module-accentVariant_1j5",
  "sSize": "AmountCounter-module-sSize_3Yv",
  "mSize": "AmountCounter-module-mSize_2_o",
  "outlineType": "AmountCounter-module-outlineType_2_k",
  "hasError": "AmountCounter-module-hasError_3d9",
  "voteType": "AmountCounter-module-voteType_3AR"
};
export const AmountCounterVariants = {
  neutral: 'neutral',
  accent: 'accent'
};
export const AmountCounterSizes = {
  s: 's',
  m: 'm'
};
export const AmountCounterTypes = {
  outline: 'outline',
  vote: 'vote'
};
export const AmountCounterIconMinusVariants = {
  minus: 'minus',
  minusRound: 'minusRound'
};
export const AmountCounterIconPlusVariants = {
  plus: 'plus',
  plusRound: 'plusRound'
};
export const AmountCounterPressedButtonVariants = {
  plus: 'plus',
  minus: 'minus'
};
export const AmountCounterModes = {
  vote: 'vote',
  enumerator: 'enumerator'
};
const patternDefault = [];
export const AmountCounter = ({
  ariaLabelDown = 'Убрать 1 товар',
  ariaLabelInput = 'Количество товара',
  ariaLabelUp = 'Добавить 1 товар',
  variant = AmountCounterVariants.neutral,
  size = AmountCounterSizes.m,
  type = AmountCounterTypes.outline,
  className,
  controlRef,
  hasError = false,
  iconMinus = AmountCounterIconMinusVariants.minus,
  iconPlus = AmountCounterIconPlusVariants.plus,
  initialValue = '0',
  innerRef,
  max = NaN,
  min = NaN,
  mode = AmountCounterModes.enumerator,
  onValueChange = () => {},
  pattern = patternDefault,
  pressedButton = null,
  readOnlyInput = false,
  step = 1,
  style
}) => {
  const inputRef = useRef(null);
  const {
    value,
    onInput,
    'data-unmasked-value': dataUnmaskedValue,
    ...other
  } = useMaskedInput({
    pattern,
    initialValue,
    inputMode: 'numeric',
    inputRef
  });
  const ownRef = useForkRef(inputRef, controlRef);
  const [hasErrorState, setHasError] = useState(hasError);
  const [pressedState, setPressed] = useState(pressedButton);
  useEffect(() => {
    if (inputRef.current) {
      onValueChange(inputRef.current.getAttribute('data-unmasked-value') || '');
    }
  }, [onValueChange, value]);
  useEffect(() => {
    if (!hasErrorState) return;
    const timer = setTimeout(() => {
      setHasError(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [hasErrorState]);
  const handleInput = useCallback(e => onInput(e), [onInput]);
  const handlerClick = useCallback(e => {
    const val = inputRef.current ? parseInt(inputRef.current.getAttribute('data-unmasked-value') || '', 10) : parseInt(initialValue, 10);
    let newValue = val;
    let newPressedButton = null;
    if (mode === 'vote') {
      if (e.target.name === 'decrease' && pressedState !== 'minus' || e.target.name === 'increase' && pressedState === 'plus') {
        newValue = newValue - step;
        newPressedButton = e.target.name === 'decrease' ? 'minus' : null;
      }
      if (pressedState === 'plus' && e.target.name === 'decrease') {
        newValue = newValue - step;
      }
      if (e.target.name === 'increase' && pressedState !== 'plus' || e.target.name === 'decrease' && pressedState === 'minus') {
        newValue = val + step;
        newPressedButton = e.target.name === 'increase' ? 'plus' : null;
      }
      if (pressedState === 'minus' && e.target.name === 'increase') {
        newValue = newValue + step;
      }
      setPressed(newPressedButton);
    }
    if (mode === 'enumerator') {
      newValue = e.target.name === 'decrease' ? val - step : val + step;
    }
    onInput(newValue.toString());
    if (mode === 'enumerator' && (newValue <= min || newValue >= max)) {
      setHasError(true);
    }
  }, [onInput, step, initialValue, max, min, mode, pressedState]);
  let sign = 'zero';
  if (Number(dataUnmaskedValue) > 0) {
    sign = 'positive';
  }
  if (Number(dataUnmaskedValue) < 0) {
    sign = 'negative';
  }
  return /*#__PURE__*/React.createElement("div", {
    style: style,
    className: cn(styles.Root, styles[`${variant}Variant`], styles[`${size}Size`], styles[`${type}Type`], {
      [styles.hasError]: hasErrorState || hasError
    }, className),
    ref: innerRef
  }, /*#__PURE__*/React.createElement("input", _extends({
    "aria-invalid": hasError,
    "aria-label": ariaLabelInput,
    className: styles.Input,
    "data-number-sign": sign,
    "data-unmasked-value": dataUnmaskedValue,
    onInput: handleInput,
    readOnly: readOnlyInput,
    ref: ownRef,
    type: "text",
    value: value
  }, other)), /*#__PURE__*/React.createElement("button", {
    "aria-label": ariaLabelDown,
    "aria-pressed": pressedState === 'minus',
    className: styles.BtnDown,
    disabled: Number(dataUnmaskedValue) <= min,
    name: "decrease",
    onClick: handlerClick,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: iconMinus,
    className: styles.Icon,
    "aria-hidden": true
  })), /*#__PURE__*/React.createElement("button", {
    "aria-label": ariaLabelUp,
    "aria-pressed": pressedState === 'plus',
    className: styles.BtnUp,
    disabled: Number(dataUnmaskedValue) >= max,
    name: "increase",
    onClick: handlerClick,
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: iconPlus,
    className: styles.Icon,
    "aria-hidden": true
  })));
};
if (process.env.NODE_ENV !== 'production') {
  AmountCounter.displayName = 'AmountCounter';
}